import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { DatePicker } from '@/components/composite/date-picker/date-picker';
import { TransactionAmount } from '@/components/composite/transaction-amount/transaction-amount';
import { TransactionTypeBadge } from '@/components/composite/transaction-type-badge/transaction-type-badge';
import { useAccountTransactions, useReportConsume } from '@/lib/api/accounts-queries';
import { formatDateTime, todayIsoDate, yesterdayIsoDate } from '@/lib/datetime';
import { useToast } from '@/lib/hooks/use-toast';
import { cn } from '@/lib/utils';
import type { AdsAccount } from '@/lib/api/types';

// 隐藏 number 输入框的原生上下微调箭头——金额按键盘输入,微调按钮无意义且割裂视觉
const NO_SPINNER =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

export function ConsumeDialog({
  account,
  onClose,
}: {
  account: AdsAccount | null;
  onClose: () => void;
}) {
  const banned = account?.status === 1;
  // 封户账户只能录入封户日期及之前的消耗(后端 assertSpendBizDateAllowed),故上限为封户日
  const banDateDay = account?.banDate?.slice(0, 10) ?? '';
  const maxDate = banned && banDateDay ? banDateDay : todayIsoDate();
  const initialBizDate = () => {
    const y = yesterdayIsoDate();
    return y > maxDate ? maxDate : y;
  };

  const [amount, setAmount] = useState('');
  const [bizDate, setBizDate] = useState(initialBizDate);
  const { toast } = useToast();
  const reportConsume = useReportConsume();
  // Stable requestId during in-flight submit so StrictMode double-invoke / re-renders
  // do not generate two ids and bypass server-side idempotency.
  const requestIdRef = useRef<string | null>(null);
  const amountRef = useRef<HTMLInputElement>(null);

  const { data: txData, isLoading: txLoading } = useAccountTransactions(
    account?.accountId ?? null,
    { pageNo: 1, pageSize: 15 },
    { enabled: !!account },
  );

  const accountId = account?.accountId;
  useEffect(() => {
    if (accountId) {
      setAmount('');
      setBizDate(initialBizDate());
    }
    return () => {
      requestIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, maxDate]);

  const amountNum = parseFloat(amount);
  const isAmountValid = Number.isFinite(amountNum) && amountNum > 0;
  const boundApp = account?.activeAppName || account?.activeAppPkg || '';
  const canSubmit =
    isAmountValid && !!bizDate && !!account?.activeAppPkg && !reportConsume.isPending;

  // 清空本次录入,回到初始状态以便继续录下一笔(不关闭抽屉)
  const resetForm = () => {
    setAmount('');
    setBizDate(initialBizDate());
    amountRef.current?.focus();
  };

  const handleSubmit = () => {
    if (!account || !isAmountValid) return;
    if (!requestIdRef.current) {
      requestIdRef.current = crypto.randomUUID();
    }
    reportConsume.mutate(
      {
        accountId: account.accountId,
        amount: amount.trim(),
        pkg: account.activeAppPkg ?? '',
        bizDate: bizDate + ' 00:00:00',
        requestId: requestIdRef.current,
      },
      {
        onSuccess: () => {
          // 保持抽屉打开:录入结果落入下方「最近流水」即为生效确认,便于连续录入多笔
          toast({ variant: 'success', description: '消耗已录入' });
          requestIdRef.current = null;
          setAmount('');
          amountRef.current?.focus();
        },
        onError: (err) => {
          toast({ variant: 'destructive', description: err.message });
          requestIdRef.current = null;
        },
      },
    );
  };

  const txRecords = txData?.records ?? [];

  return (
    <Sheet open={!!account} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[560px]">
        <SheetHeader className="space-y-1 border-b px-6 py-4">
          <SheetTitle className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            录入消耗
          </SheetTitle>
          <SheetDescription>
            {account?.accountName} · <span className="font-mono">{account?.accountCode}</span>
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {/* 第一层:确认账户上下文(只读) */}
          {banned && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                该账户已封户{banDateDay ? `（封户日：${banDateDay}）` : ''}
                ，仅可录入封户日期及之前的消耗。
              </span>
            </div>
          )}
          <div className="space-y-2">
            <Label>绑定 App</Label>
            <div
              className={cn(
                'flex h-10 items-center truncate rounded-md border border-input bg-muted/40 px-3 font-mono text-[13px]',
                boundApp ? 'text-foreground' : 'text-muted-foreground',
              )}
              title={boundApp || undefined}
            >
              {boundApp || '未绑定 App'}
            </div>
          </div>

          {/* 第二层:录入内容(重点),操作按钮就近放置便于连续录入 */}
          <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
            <div className="space-y-2">
              <Label htmlFor="consume-amount">消耗金额</Label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-xl font-semibold text-muted-foreground">
                  $
                </span>
                <Input
                  ref={amountRef}
                  id="consume-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  autoFocus
                  className={cn(
                    'h-14 bg-background pl-9 pr-16 text-2xl font-bold tabular-nums',
                    NO_SPINNER,
                  )}
                />
                <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground">
                  USD
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="consume-bizdate">业务日期</Label>
              <div className="flex items-center gap-1.5">
                <DatePicker
                  value={bizDate}
                  onChange={setBizDate}
                  max={maxDate}
                  className="flex-1"
                />
                {(banned && banDateDay
                  ? ([['封户日', banDateDay]] as [string, string][])
                  : ([
                      ['昨日', yesterdayIsoDate()],
                      ['今日', todayIsoDate()],
                    ] as [string, string][])
                ).map(([label, val]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => setBizDate(val)}
                    className={cn(
                      'h-8 shrink-0 rounded-md border px-2 text-[12px] font-medium transition-colors',
                      bizDate === val
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-input bg-background text-foreground/70 hover:bg-accent hover:text-accent-foreground',
                    )}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                onClick={resetForm}
                disabled={!amount || reportConsume.isPending}
              >
                清空
              </Button>
              <Button onClick={handleSubmit} disabled={!canSubmit}>
                {reportConsume.isPending ? '录入中…' : '确认录入'}
              </Button>
            </div>
          </div>

          {/* 第三层:最近流水(录入生效的确认) */}
          <div className="space-y-2">
            <p className="text-[13px] font-medium text-muted-foreground">最近流水</p>
            <div className="overflow-hidden rounded-md border">
              {txLoading ? (
                <div className="space-y-2 p-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-7 w-full" />
                  ))}
                </div>
              ) : txRecords.length === 0 ? (
                <div className="py-10 text-center text-[13px] text-muted-foreground">暂无流水</div>
              ) : (
                <div className="max-h-[240px] overflow-y-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-2 text-left font-medium">业务日期</th>
                        <th className="px-3 py-2 text-left font-medium">创建时间</th>
                        <th className="px-3 py-2 text-left font-medium">类型</th>
                        <th className="px-3 py-2 text-right font-medium">金额 (USD)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {txRecords.map((tx) => (
                        <tr
                          key={tx.id}
                          className="border-b transition-colors last:border-0 hover:bg-muted/30"
                        >
                          <td className="px-3 py-2 font-mono tabular-nums text-muted-foreground">
                            {tx.bizDate ?? '-'}
                          </td>
                          <td className="px-3 py-2 text-[12px] tabular-nums text-muted-foreground">
                            {formatDateTime(tx.createTime)}
                          </td>
                          <td className="px-3 py-2">
                            <TransactionTypeBadge type={tx.type} reversed={tx.reversed} compact />
                          </td>
                          <td className="px-3 py-2">
                            <TransactionAmount tx={tx} className="justify-end font-semibold" />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
